/**
 * i18next Configuration
 * Internationalization setup for English, Portuguese, and Spanish
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enTranslation from '../locales/en/translation.json';
import ptTranslation from '../locales/pt/translation.json';
import esTranslation from '../locales/es/translation.json';
import enCities from '../locales/en/cities.json';
import ptCities from '../locales/pt/cities.json';
import esCities from '../locales/es/cities.json';
import enCountries from '../locales/en/countries.json';
import ptCountries from '../locales/pt/countries.json';
import esCountries from '../locales/es/countries.json';
import enDialogues from '../locales/en/dialogues.json';
import ptDialogues from '../locales/pt/dialogues.json';
import esDialogues from '../locales/es/dialogues.json';
import enQuizzes from '../locales/en/quizzes.json';
import ptQuizzes from '../locales/pt/quizzes.json';
import esQuizzes from '../locales/es/quizzes.json';

// Language persistence key
const LANGUAGE_STORAGE_KEY = 'ponder-flight-game-language';

/**
 * The languages this client ships translations for, in the order a player
 * meets them, each with the key of its name, written in that language in every
 * locale. Settings and the language control on a way in whose link names none
 * offer them, both through useLanguageChoice
 * (components/common/use-language-choice.tsx); nothing else spells them or
 * reads them (`language-list-by-hand`).
 */
export const LANGUAGES = [
  { code: 'en', nameKey: 'login.languageEnglish' },
  { code: 'pt', nameKey: 'login.languagePortuguese' },
  { code: 'es', nameKey: 'login.languageSpanish' },
] as const;

/** One of the languages the client ships. */
export type Language = (typeof LANGUAGES)[number]['code'];

const SUPPORTED_LANGUAGES: readonly string[] = LANGUAGES.map((language) => language.code);
const DEFAULT_LANGUAGE: Language = 'en';

const isSupported = (value: string): value is Language => SUPPORTED_LANGUAGES.includes(value);

/**
 * The language a link names, if it names one the client ships. Two
 * parameters can carry it: `lang` (the portal's UI language) and `language`
 * (the session's transcription language, set by the moderator). They describe
 * the same session language, so either will do — `lang` is preferred only
 * because it is the more specific of the two. A way in whose link names none
 * offers the language control instead (components/login/entry-frame.tsx).
 */
export function linkLanguage(search: string): Language | null {
  const params = new URLSearchParams(search);
  for (const param of ['lang', 'language'] as const) {
    const candidate = params.get(param)?.toLowerCase().split('-')[0];
    if (candidate && isSupported(candidate)) return candidate;
  }
  return null;
}

/**
 * Resolve the startup language.
 *
 * The language named on the launch URL is authoritative and beats anything
 * this browser stored in an earlier session; a link that names none starts in
 * the language this browser last chose.
 */
function resolveInitialLanguage(): string {
  let named: Language | null = null;
  try {
    named = linkLanguage(window.location.search);
  } catch {
    named = null;
  }
  if (named) {
    // Persist so it survives an in-match refresh, which drops query params.
    localStorage.setItem(LANGUAGE_STORAGE_KEY, named);
    return named;
  }

  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && isSupported(stored)) {
    return stored;
  }

  return DEFAULT_LANGUAGE;
}

const savedLanguage = resolveInitialLanguage();

/**
 * Initialize i18next with English, Portuguese, and Spanish translations
 */
i18n
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    resources: {
      en: {
        translation: enTranslation,
        cities: enCities,
        countries: enCountries,
        dialogues: enDialogues,
        quizzes: enQuizzes,
      },
      pt: {
        translation: ptTranslation,
        cities: ptCities,
        countries: ptCountries,
        dialogues: ptDialogues,
        quizzes: ptQuizzes,
      },
      es: {
        translation: esTranslation,
        cities: esCities,
        countries: esCountries,
        dialogues: esDialogues,
        quizzes: esQuizzes,
      },
    },
    ns: ['translation', 'cities', 'countries', 'dialogues', 'quizzes'],
    defaultNS: 'translation',
    lng: savedLanguage, // Use saved language from localStorage
    fallbackLng: 'en', // Fallback language if translation is missing
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    debug: import.meta.env.DEV, // Enable debug mode in development
  });

// The page says which language it is in, so the browser hyphenates a word by
// its own language's rules and a screen reader speaks it in that voice. (No
// page under the unit tests, which import this for the strings alone.)
const markPageLanguage = (lng: string) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
};
markPageLanguage(i18n.language);

// Save language to localStorage whenever it changes
i18n.on('languageChanged', (lng) => {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  markPageLanguage(lng);
  console.log('[i18n] Language changed to:', lng);
});

export default i18n;
